import { loggerCan } from '@/data/utils/loggerUtil';
import { ContentType, HttpClient, RequestParams } from './http-client';
import { logger } from '@/logging/logger';
import {
	DpValeExistingUser,
	DpvaleGrandRoleBody,
	DpvaleLoginPhone,
	DpvaleLoginPhoneBody,
	DpvaleLoginPhoneSendOtp,
	DpvaleLoginPhoneSendOtpBody,
	DpvalePromotion,
	DpvaleRecurrente,
	DpvaleRecurrenteBody,
	DpValeRedeemerForRegister,
	DpValesDisponibles,
	DpvaleSendSmsPhoneVerification,
	DpvaleSendSmsPhoneVerificationBody,
	DpvaleSmsPhoneVerification,
	DpvaleSmsPhoneVerificationBody,
	DpvaleUser,
	DpvaleValidateToken,
	DpvaleValidateTokenBody,
	DpvaleVerifyCredentialsBody,
	PostDpvalePromotionBody,
} from './data-contracts';

export class Dpvale<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;
	traceDetails: string | undefined;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
		this.traceDetails = process.env.TRACE_DETAILS?.trim();
	}

	/**
	 * @tags transactions
	 * @name postDpvalePromotions
	 * @summary Post obtain information of dpvale promotion by product
	 * @request POST:/store/{storeId}/dpvale/promotions
	 * @secure
	 * @description Post obtain information of dpvale promotion by product
	 */
	postDpvalePromotion = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		data?: PostDpvalePromotionBody,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('postDpvalePromotion'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'postDpvalePromotion',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvalePromotion, void>({
			path: `/store/${storeId}/dpvale/promotions`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValesDisponibles = (
		storeId: string,
		query?: Record<string, unknown>,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValesDisponibles'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'dpValesDisponibles',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpValesDisponibles, void>({
			path: `/store/${storeId}/dptienda/vales-disponibles`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeRecurrente = (
		storeId: string,
		data?: DpvaleRecurrenteBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeRecurrente'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeRecurrente',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleRecurrente, void>({
			path: `/store/${storeId}/dptienda/vale-recurrente`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeValidateToken = (
		storeId: string,
		data?: DpvaleValidateTokenBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeValidateToken'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeValidateToken',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleValidateToken, void>({
			path: `/store/${storeId}/dptienda/token/validate`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeSmsPhoneVerification = (
		storeId: string,
		data?: DpvaleSmsPhoneVerificationBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeSmsPhoneVerification'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeSmsPhoneVerification',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleSmsPhoneVerification, void>({
			path: `/store/${storeId}/sms/phone-verification/validate-code?v=2`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeSendSmsPhoneVerification = (
		storeId: string,
		data?: DpvaleSendSmsPhoneVerificationBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeSendSmsPhoneVerification'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeSendSmsPhoneVerification',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleSendSmsPhoneVerification, void>({
			path: `/store/${storeId}/sms/phone-verification/send-sms?v=2`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeExistingUser = (
		storeId: string,
		email: string,
		query?: Record<string, unknown>,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeExistingUser'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'dpValeExistingUser',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpValeExistingUser, void>({
			path: `/store/${storeId}/canjeante/existing-user/${email}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeRedeemerForRegister = (
		storeId: string,
		redeemerId: string | number,
		query?: Record<string, unknown>,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeRedeemerForRegister'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'dpValeRedeemerForRegister',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpValeRedeemerForRegister, void>({
			path: `/store/${storeId}/canjeante/for-register/${redeemerId}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeGrandRole = (
		storeId: string,
		data?: DpvaleGrandRoleBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeGrandRole'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeGrandRole',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<unknown, void>({
			path: `/store/${storeId}/dptienda/registro/grant-role`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeVerifyCredentials = (
		storeId: string,
		data?: DpvaleVerifyCredentialsBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeVerifyCredentials'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeVerifyCredentials',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<unknown, void>({
			path: `/store/${storeId}/dptienda/registro/verify-credentials`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeLoginPhoneSendOtp = (
		storeId: string,
		data?: DpvaleLoginPhoneSendOtpBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeLoginPhoneSendOtp'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeLoginPhoneSendOtp',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleLoginPhoneSendOtp, void>({
			path: `/store/${storeId}/login-phone/send-otp`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeLoginPhone = (
		storeId: string,
		data?: DpvaleLoginPhoneBody,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (
			loggerCan('trace') &&
			(!this.traceDetails || this.traceDetails.includes('dpValeLoginPhone'))
		) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: data ?? {},
				methodName: 'dpValeLoginPhone',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleLoginPhone, void>({
			path: `/store/${storeId}/login-phone`,
			method: 'PUT',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};

	dpValeUser = (storeId: string, query?: Record<string, unknown>, params: RequestParams = {}) => {
		const { _requestId: requestId } = params as any;
		delete (params as any)._requestId;

		if (loggerCan('trace') && (!this.traceDetails || this.traceDetails.includes('dpValeUser'))) {
			const paramsLogger = logger.child({
				params,
				query: query ?? {},
				body: null ?? {},
				methodName: 'dpValeUser',
				requestId,
			});
			paramsLogger.trace('API request parameters');
		}
		return this.http.request<DpvaleUser, void>({
			path: `/store/${storeId}/canjeante`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			...params,
		});
	};
}
